import {useState} from "react";
import lscache from "lscache";

import {KM_PHONE_VERIFICATION_TEST_IDS} from "@pg-mono/test-ids";
import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {useAppDispatch} from "../../../../store/hooks/use_app_dispatch";
import {useAppSelector} from "../../../../store/hooks/use_app_selector";
import {PHONE_VERIFICATION_MODAL_DISMISSED} from "../../../km_app/constants/storage_keys";
import {setAmplitudePhoneVerifiedUserProperty} from "../../../tracking/amplitude/set_amplitude_phone_verified_user_property";
import {UserAmplitudeTracker} from "../../../tracking/amplitude/user_amplitude_tracker";
import {useGetUserProfileQuery} from "../../api/user_api";
import {usePhoneVerificationActionExecutor} from "../../phone_verification/hooks/use_phone_verification_action_executor";
import {usePhoneVerificationModalOrchestration} from "../../phone_verification/hooks/use_phone_verification_modal_orchestration";
import {useRegistrationOnboardingPhone} from "../../phone_verification/hooks/use_registration_onboarding_phone";
import {PhoneVerificationPendingActionType} from "../../phone_verification/types/phone_verification_types";
import {UserPhoneVerificationProvider} from "../../providers/UserPhoneVerificationProvider";
import {
    handleCloseRegistrationPhoneNumberModal,
    handleCloseUpdatePhoneNumberModal,
    handleOpenRegistrationPhoneNumberModal,
    handleOpenUpdatePhoneNumberModal,
    phoneVerificationCancelled,
    phoneVerificationSucceeded,
    reopenPhoneVerificationModal,
    selectPendingAction
} from "../../state/user_phone_verification_slice";
import {USER_PHONE_NUMBER_MODAL_PROGRESS, UserPhoneNumberModal} from "../modal/UserPhoneNumberModal";
import {UserPhoneSmsModal} from "../modal/UserPhoneSmsModal";

export const UserPhoneVerificationFlow = () => {
    // Must run on create-account too (not only AuthenticatedLayout) so registration can complete after SMS.
    usePhoneVerificationActionExecutor();

    const {data: userProfile, refetch: refetchUserProfile} = useGetUserProfileQuery();
    const dispatch = useAppDispatch();
    const pendingAction = useAppSelector(selectPendingAction);
    const isPhoneVerificationModalSuppressed = useAppSelector((state) => state.userPhoneVerification.isPhoneVerificationModalSuppressed);
    const {
        phoneNumber,
        hasPhone,
        isLeadSendPending,
        openPhoneVerificationModal,
        openUpdatePhoneNumberModal,
        beginSkipUpdatePhonePrompt,
        endSkipUpdatePhonePrompt
    } = usePhoneVerificationModalOrchestration({userProfile, refetchUserProfile});
    const registrationOnboardingPhone = useRegistrationOnboardingPhone({beginSkipUpdatePhonePrompt, refetchUserProfile});
    const [registrationPhone, setRegistrationPhone] = useState<string | null>(null);
    const isRegistrationOnboarding = pendingAction.type === PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING;
    const showRegistrationPhone = registrationOnboardingPhone.isOpen && !isPhoneVerificationModalSuppressed;
    const showRegistrationSms =
        openPhoneVerificationModal && hasPhone && !isPhoneVerificationModalSuppressed && isRegistrationOnboarding && !showRegistrationPhone;

    if (!userProfile) return null;

    const handlers = {
        updatePhoneNumberSuccess: () => {
            clearPhoneVerificationTimerLS();
            beginSkipUpdatePhonePrompt();
            dispatch(handleCloseUpdatePhoneNumberModal());

            // Avoid awaiting a stale refetch before SMS reopen (race reopens the phone number modal).
            lscache.remove(PHONE_VERIFICATION_MODAL_DISMISSED);
            dispatch(reopenPhoneVerificationModal());
            void refetchUserProfile();
        },
        updatePhoneNumberClose: () => {
            endSkipUpdatePhonePrompt();
            lscache.set(PHONE_VERIFICATION_MODAL_DISMISSED, true);
            dispatch(handleCloseUpdatePhoneNumberModal());
            dispatch(phoneVerificationCancelled());
        },
        phoneVerificationClose: () => {
            setRegistrationPhone(null);
            endSkipUpdatePhonePrompt();
            setAmplitudePhoneVerifiedUserProperty(false);

            lscache.set(PHONE_VERIFICATION_MODAL_DISMISSED, true);
            dispatch(handleCloseUpdatePhoneNumberModal());
            dispatch(phoneVerificationCancelled());
        },
        changeNumberOpen: () => {
            endSkipUpdatePhonePrompt();
            dispatch(handleOpenUpdatePhoneNumberModal());
        },
        phoneVerificationSuccess: () => {
            setRegistrationPhone(null);
            const location = pendingAction.type === PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING && pendingAction.location;

            UserAmplitudeTracker.phoneVerified(location);
            dispatch(phoneVerificationSucceeded());
        },
        registrationPhoneClose: () => {
            if (openPhoneVerificationModal) {
                dispatch(handleCloseRegistrationPhoneNumberModal());
                return;
            }

            registrationOnboardingPhone.onClose();
        },
        registrationChangeNumber: () => {
            if (pendingAction.type !== PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING) return;

            dispatch(handleOpenRegistrationPhoneNumberModal(pendingAction.location));
        },
        registrationPhoneSaved: (savedPhone: string) => {
            setRegistrationPhone(savedPhone);
            registrationOnboardingPhone.onSuccess();
        }
    };

    return (
        <UserPhoneVerificationProvider>
            <div data-testid={KM_PHONE_VERIFICATION_TEST_IDS.MODAL}>
                <UserPhoneSmsModal
                    isOpen={openPhoneVerificationModal && hasPhone && !isPhoneVerificationModalSuppressed && !isRegistrationOnboarding}
                    onChangeNumber={handlers.changeNumberOpen}
                    onClose={handlers.phoneVerificationClose}
                    onSuccess={handlers.phoneVerificationSuccess}
                    phone={phoneNumber || undefined}
                />
            </div>
            <UserPhoneNumberModal
                isOpen={openUpdatePhoneNumberModal && !isLeadSendPending && !isPhoneVerificationModalSuppressed}
                onCloseButton={handlers.updatePhoneNumberClose}
                onSuccess={handlers.updatePhoneNumberSuccess}
                progress={USER_PHONE_NUMBER_MODAL_PROGRESS}
            />
            <UserPhoneNumberModal
                isOpen={showRegistrationPhone}
                onCloseButton={handlers.registrationPhoneClose}
                onSuccess={handlers.registrationPhoneSaved}
                progress={USER_PHONE_NUMBER_MODAL_PROGRESS}
            />
            <UserPhoneSmsModal
                isOpen={showRegistrationSms}
                onChangeNumber={handlers.registrationChangeNumber}
                onClose={handlers.phoneVerificationClose}
                onSuccess={handlers.phoneVerificationSuccess}
                phone={registrationPhone || phoneNumber || ""}
            />
        </UserPhoneVerificationProvider>
    );
};
import {useState} from "react";
import lscache from "lscache";

import {KM_PHONE_VERIFICATION_TEST_IDS} from "@pg-mono/test-ids";
import {clearPhoneVerificationTimerLS} from "@pg-mono/user";

import {useAppDispatch} from "../../../../store/hooks/use_app_dispatch";
import {useAppSelector} from "../../../../store/hooks/use_app_selector";
import {PHONE_VERIFICATION_MODAL_DISMISSED} from "../../../km_app/constants/storage_keys";
import {setAmplitudePhoneVerifiedUserProperty} from "../../../tracking/amplitude/set_amplitude_phone_verified_user_property";
import {UserAmplitudeTracker} from "../../../tracking/amplitude/user_amplitude_tracker";
import {useGetUserProfileQuery} from "../../api/user_api";
import {usePhoneVerificationActionExecutor} from "../../phone_verification/hooks/use_phone_verification_action_executor";
import {usePhoneVerificationModalOrchestration} from "../../phone_verification/hooks/use_phone_verification_modal_orchestration";
import {useRegistrationOnboardingPhone} from "../../phone_verification/hooks/use_registration_onboarding_phone";
import {PhoneVerificationPendingActionType} from "../../phone_verification/types/phone_verification_types";
import {UserPhoneVerificationProvider} from "../../providers/UserPhoneVerificationProvider";
import {
    handleCloseRegistrationPhoneNumberModal,
    handleCloseUpdatePhoneNumberModal,
    handleOpenRegistrationPhoneNumberModal,
    handleOpenUpdatePhoneNumberModal,
    phoneVerificationCancelled,
    phoneVerificationSucceeded,
    reopenPhoneVerificationModal,
    selectPendingAction
} from "../../state/user_phone_verification_slice";
import {USER_PHONE_NUMBER_MODAL_PROGRESS, UserPhoneNumberModal} from "../modal/UserPhoneNumberModal";
import {UserPhoneSmsModal} from "../modal/UserPhoneSmsModal";

export const UserPhoneVerificationFlow = () => {
    // Must run on create-account too (not only AuthenticatedLayout) so registration can complete after SMS.
    usePhoneVerificationActionExecutor();

    const {data: userProfile, refetch: refetchUserProfile} = useGetUserProfileQuery();
    const dispatch = useAppDispatch();
    const pendingAction = useAppSelector(selectPendingAction);
    const isPhoneVerificationModalSuppressed = useAppSelector((state) => state.userPhoneVerification.isPhoneVerificationModalSuppressed);
    const {
        phoneNumber,
        hasPhone,
        isLeadSendPending,
        openPhoneVerificationModal,
        openUpdatePhoneNumberModal,
        beginSkipUpdatePhonePrompt,
        endSkipUpdatePhonePrompt
    } = usePhoneVerificationModalOrchestration({userProfile, refetchUserProfile});
    const registrationOnboardingPhone = useRegistrationOnboardingPhone({beginSkipUpdatePhonePrompt, refetchUserProfile});
    const [registrationPhone, setRegistrationPhone] = useState<string | null>(null);
    const isRegistrationOnboarding = pendingAction.type === PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING;
    const showRegistrationPhone = registrationOnboardingPhone.isOpen && !isPhoneVerificationModalSuppressed;
    const showRegistrationSms =
        openPhoneVerificationModal && hasPhone && !isPhoneVerificationModalSuppressed && isRegistrationOnboarding && !showRegistrationPhone;

    if (!userProfile) return null;

    const handlers = {
        updatePhoneNumberSuccess: () => {
            clearPhoneVerificationTimerLS();
            beginSkipUpdatePhonePrompt();
            dispatch(handleCloseUpdatePhoneNumberModal());

            // Avoid awaiting a stale refetch before SMS reopen (race reopens the phone number modal).
            lscache.remove(PHONE_VERIFICATION_MODAL_DISMISSED);
            dispatch(reopenPhoneVerificationModal());
            void refetchUserProfile();
        },
        updatePhoneNumberClose: () => {
            endSkipUpdatePhonePrompt();
            lscache.set(PHONE_VERIFICATION_MODAL_DISMISSED, true);
            dispatch(handleCloseUpdatePhoneNumberModal());
            dispatch(phoneVerificationCancelled());
        },
        phoneVerificationClose: () => {
            setRegistrationPhone(null);
            endSkipUpdatePhonePrompt();
            setAmplitudePhoneVerifiedUserProperty(false);

            lscache.set(PHONE_VERIFICATION_MODAL_DISMISSED, true);
            dispatch(handleCloseUpdatePhoneNumberModal());
            dispatch(phoneVerificationCancelled());
        },
        changeNumberOpen: () => {
            endSkipUpdatePhonePrompt();
            dispatch(handleOpenUpdatePhoneNumberModal());
        },
        phoneVerificationSuccess: () => {
            setRegistrationPhone(null);
            const location = pendingAction.type === PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING && pendingAction.location;

            UserAmplitudeTracker.phoneVerified(location);
            dispatch(phoneVerificationSucceeded());
        },
        registrationPhoneClose: () => {
            if (openPhoneVerificationModal) {
                dispatch(handleCloseRegistrationPhoneNumberModal());
                return;
            }

            registrationOnboardingPhone.onClose();
        },
        registrationChangeNumber: () => {
            if (pendingAction.type !== PhoneVerificationPendingActionType.REGISTRATION_ONBOARDING) return;

            dispatch(handleOpenRegistrationPhoneNumberModal(pendingAction.location));
        },
        registrationPhoneSaved: (savedPhone: string) => {
            setRegistrationPhone(savedPhone);
            registrationOnboardingPhone.onSuccess();
        }
    };

    return (
        <UserPhoneVerificationProvider>
            <div data-testid={KM_PHONE_VERIFICATION_TEST_IDS.MODAL}>
                <UserPhoneSmsModal
                    isOpen={openPhoneVerificationModal && hasPhone && !isPhoneVerificationModalSuppressed && !isRegistrationOnboarding}
                    onChangeNumber={handlers.changeNumberOpen}
                    onClose={handlers.phoneVerificationClose}
                    onSuccess={handlers.phoneVerificationSuccess}
                    phone={phoneNumber || undefined}
                />
            </div>
            <UserPhoneNumberModal
                isOpen={openUpdatePhoneNumberModal && !isLeadSendPending && !isPhoneVerificationModalSuppressed}
                onCloseButton={handlers.updatePhoneNumberClose}
                onSuccess={handlers.updatePhoneNumberSuccess}
                progress={USER_PHONE_NUMBER_MODAL_PROGRESS}
            />
            <UserPhoneNumberModal
                isOpen={showRegistrationPhone}
                onCloseButton={handlers.registrationPhoneClose}
                onSuccess={handlers.registrationPhoneSaved}
                progress={USER_PHONE_NUMBER_MODAL_PROGRESS}
            />
            <UserPhoneSmsModal
                isOpen={showRegistrationSms}
                onChangeNumber={handlers.registrationChangeNumber}
                onClose={handlers.phoneVerificationClose}
                onSuccess={handlers.phoneVerificationSuccess}
                phone={registrationPhone || phoneNumber || ""}
            />
        </UserPhoneVerificationProvider>
    );
};
